import React from 'react';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';

export const AppContext = React.createContext();

// Check if user is already logged in from auth cookie
const getInitialAuthState = () => {
	const authStatus = Cookies.get('auth-status');

	// Fallback to stored client ID for development, then default
	const storedClientId = Cookies.get('client-id') || import.meta.env.VITE_DEFAULT_CLIENT_ID || '00000000-0000-0000-0000-000000000001';

	// Get dark mode preference from localStorage
	const darkModePreference = localStorage.getItem('dark-mode');
	const isDarkMode = darkModePreference === 'true';

	return {
		loggedIn: authStatus === 'authenticated',
		clientId: storedClientId, // Will be updated after user fetch
		user: null, // Will be loaded asynchronously
		isDevelopmentMode: import.meta.env.DEV,
		sidebarCollapsed: false, // Default to expanded sidebar
		darkMode: isDarkMode // Dark mode preference
	};
};

const initialState = getInitialAuthState();

function reducer(state, action) {
	switch (action.type) {
	case 'log-in':{
		// Use client ID from action or existing, then default
		const clientId = action.clientId || state.clientId || '00000000-0000-0000-0000-000000000001';
		// Store client ID in cookie for persistence (for development override)
		if (action.clientId) {
			Cookies.set('client-id', clientId, {
				secure: import.meta.env.PROD,
				sameSite: 'strict',
				expires: 30 // 30 days
			});
		}

		return {
			...state,
			loggedIn: action.loggedIn,
			clientId,
			user: action.user || state.user // Store user details if provided
		};
	}
	case 'log-out':{
		// Remove auth status cookie on logout
		Cookies.remove('auth-status');
		// Keep client-id for development purposes
		return {
			...state,
			loggedIn: false,
			user: null // Clear user details
		};
	}
	case 'set-client':{
		// Update client ID and store in cookie (development mode)
		Cookies.set('client-id', action.clientId, {
			secure: import.meta.env.PROD,
			sameSite: 'strict',
			expires: 30 // 30 days
		});
		return {
			...state,
			clientId: action.clientId
		};
	}	case 'set-user':{
		// Set user details and extract client ID
		const clientId = action.user?.ClientId || action.user?.clientId || state.clientId;

		return {
			...state,
			user: action.user,
			clientId,
			loggedIn: !!action.user
		};
	}
	case 'toggle-sidebar':{
		return {
			...state,
			sidebarCollapsed: action.collapsed !== undefined ? action.collapsed : !state.sidebarCollapsed
		};
	}
	case 'toggle-dark-mode':{
		const newDarkMode = action.darkMode !== undefined ? action.darkMode : !state.darkMode;
		// Persist to localStorage
		localStorage.setItem('dark-mode', newDarkMode.toString());
		// Update HTML data-bs-theme attribute for Bootstrap
		document.documentElement.setAttribute('data-bs-theme', newDarkMode ? 'dark' : 'light');
		return {
			...state,
			darkMode: newDarkMode
		};
	}
	default:
		throw new Error(`unrecognized action: ${action.type}`);
	}
}

export const AppProvider = ({ children }) => {
	const [state, dispatch] = React.useReducer(reducer, initialState);

	const logIn = (loggedIn, clientId, user) => {
		dispatch({
			type: 'log-in',
			loggedIn,
			clientId,
			user
		});
	};

	const logOut = () => {
		dispatch({
			type: 'log-out'
		});
	};
	const setClientId = (clientId) => {
		dispatch({
			type: 'set-client',
			clientId
		});
	};

	const setUser = (user) => {
		dispatch({
			type: 'set-user',
			user
		});
	};

	const toggleSidebar = (collapsed) => {
		dispatch({
			type: 'toggle-sidebar',
			collapsed
		});
	};

	const toggleDarkMode = (darkMode) => {
		dispatch({
			type: 'toggle-dark-mode',
			darkMode
		});
	};

	// Apply dark mode on initial load
	React.useEffect(() => {
		document.documentElement.setAttribute('data-bs-theme', state.darkMode ? 'dark' : 'light');
	}, []);

	return (
		<AppContext.Provider
			value={{
				state,
				actions: {
					logIn,
					logOut,
					setClientId,
					setUser,
					toggleSidebar,
					toggleDarkMode
				}
			}}
		>
			{children}
		</AppContext.Provider>
	);
};

AppProvider.propTypes = {
	children: PropTypes.node.isRequired
};
